import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function Login() {
  const { loginWithCredential, devLogin, isDevAuth, user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [devEmail, setDevEmail] = useState('');

  const handleLoginSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      await loginWithCredential(credentialResponse);
      toast.success('Signed in successfully');
      navigate('/tbs-dashboard', { replace: true });
    } catch (e) {
      toast.error('Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginError = () => {
    toast.error('Google sign in failed');
  };

  const handleDevLogin = () => {
    devLogin(devEmail.trim() || 'dev@example.com');
    toast.success('Signed in (dev mode)');
    navigate('/tbs-dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050506] text-white flex items-center justify-center relative overflow-hidden">
      <div className="halftone" />
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(600px circle at 50% 30%, rgba(46,107,255,0.15), transparent 60%)'
      }} />
      <div className="relative z-10 w-full max-w-[420px] px-6">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10 hover:opacity-80 transition-opacity">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="12" cy="12" r="3" fill="currentColor" />
          </svg>
          <span className="text-[16px] tracking-tight">grocera</span>
        </Link>

        <div className="border border-white/10 rounded-lg p-8 bg-black/40 backdrop-blur">
          <p className="eyebrow mb-3">Sign in</p>
          <h1 className="h-display text-[32px] leading-[1.1] mb-3">Open your Grocera dashboard.</h1>
          <p className="text-white/60 text-[14px] leading-[1.6] mb-8">
            Sign in with Google to preview the dashboard. Choose your plan on the next step.
          </p>

          {user ? (
            <Link to="/tbs-dashboard" className="btn-primary w-full justify-center">
              Continue to dashboard <ArrowRight size={16} />
            </Link>
          ) : (
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
                theme="dark"
              />
            </div>
          )}

          <div className="mono text-[10.5px] text-white/40 mt-6 text-center tracking-wider">
            SECURE OAUTH · SESSIONS LAST 7 DAYS
          </div>

          {isDevAuth && !user && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="mono text-[10.5px] text-white/40 mb-3 tracking-wider">DEV MODE · SKIP GOOGLE OAUTH</p>
              <div className="flex gap-2">
                <input
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-[13px] text-white outline-none focus:border-white/30"
                />
                <button onClick={handleDevLogin} className="btn-ghost text-[12px] whitespace-nowrap px-3">
                  Continue (dev)
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-white/40 text-[12px] text-center mt-6">
          By continuing you agree to Grocera&rsquo;s terms and privacy.
        </p>
      </div>
    </div>
  );
}
